/**
 * Save Content to Watch Later UseCase
 *
 * Handles saving/removing content from watch later following Flutter's pattern:
 * 1. Optimistically update SecureStorage (for instant UI feedback)
 * 2. Call mediaService to update Firestore
 * 3. Revert SecureStorage if Firestore operation fails
 *
 * Pattern from Flutter: MediasRepository.saveContent
 * Source: lib/data/repositories/medias_repository.dart
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { MediaService } from '@/domain/services/media/mediaService';
import type { SavedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { SavedContentRemapper } from '@/domain/remappers/savedContent/savedContentRemapper';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class SaveContentToWatchLaterUseCase {
  private storage: SecureStorageWebCrypto;
  private mediaService: MediaService;

  constructor(storage?: SecureStorageWebCrypto, mediaService?: MediaService) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
    this.mediaService = mediaService || new MediaService();
  }

  /**
   * Toggle save state for a content item (add/remove from watch later)
   *
   * @param media - The media entity to save/unsave
   * @returns Promise<boolean> - Success status
   */
  async execute(media: UserMediaEntity): Promise<boolean> {
    try {
      const storageKey = SECURE_STORAGE_KEYS.SAVED_CONTENT + media.relativePath;
      const currentSavedContent = await this.storage.getEncrypted<SavedContentStorage>(storageKey);

      const isCurrentlySaved = currentSavedContent !== null;
      const shouldSave = !isCurrentlySaved;

      const entity = SavedContentRemapper.fromUserMedia(media);
      const storageEntity = SavedContentRemapper.toStorageEntity(entity);

      // Step 1: Optimistically update local storage for instant UI feedback
      let newSavedContent: SavedContentStorage | null = null;

      if (shouldSave) {
        newSavedContent = storageEntity;
        await this.storage.setEncrypted(storageKey, newSavedContent);
      } else {
        await this.storage.remove(storageKey);
      }

      // Step 2: Call mediaService to update Firestore
      try {
        const success = await this.mediaService.saveContent(entity, shouldSave);

        if (!success) {
          throw new Error('Firestore operation returned success: false');
        }
      } catch (firestoreError) {
        // Step 3: Revert storage on failure
        appLogger.error('Firestore error, reverting local storage', 'SaveContentToWatchLaterUseCase.execute', { error: firestoreError as Error });

        if (currentSavedContent) {
          await this.storage.setEncrypted(storageKey, currentSavedContent);
        } else {
          await this.storage.remove(storageKey);
        }

        return false;
      }

      return true;
    } catch (error) {
      appLogger.error('Error saving content to watch later', 'SaveContentToWatchLaterUseCase.execute', { error: error as Error });
      return false;
    }
  }
}

// Export singleton instance
export const saveContentToWatchLaterUseCase = new SaveContentToWatchLaterUseCase();
